/**
 * Centralized tag mapping utilities.
 * Maps API tag responses (which may use snake_case or camelCase) to consistent UI formats.
 * All type fields are normalized to lowercase for consistent filtering/display.
 *
 * IMPORTANT: All components should import Tag types from here instead of declaring local interfaces.
 */

// --- Full tag shape (TagsPage, TagList, TagCard, TagGrid, EditTagDialog) ---
export interface MappedTag {
  id: string;
  name: string;
  description: string;
  type: string;
  color: string;
  icon?: string;
  criteria: any;
  created_at: string;
  created_by: string;
  customers: string[];
}

export function mapApiTag(t: any): MappedTag {
  // Derive customers array: prefer explicit customers, fall back to tenantId/tenant_id
  let customers: string[] = t.customers || [];
  if (customers.length === 0) {
    const tenantId = t.tenantId || t.tenant_id;
    if (tenantId) {
      customers = [tenantId];
    }
  }

  return {
    id: t.tag_id || t.tagId || t.id || "",
    name: t.tag_name || t.tagName || t.name || "",
    description: t.tag_description || t.description || "",
    type: String(t.tag_type || t.tagType || t.type || "static").toLowerCase(),
    color: t.tag_color || t.color || "#6b7280",
    icon: t.tag_icon || t.icon,
    criteria: t.tag_criteria || t.criteria || null,
    created_at: t.created_at || t.createdAt || new Date().toISOString(),
    created_by: t.created_by || t.createdBy || "system",
    customers,
  };
}

// --- Minimal shape (TagsDisplay, DeviceTable, TagFilter, ColumnTagFilter, useTagAssignment, TagRemovalDialog) ---
export interface MappedTagMinimal {
  id: string;
  name: string;
  color: string;
  icon?: string;
  type: string;
  description?: string;
  /** Owning tenant id, when the API exposes it. Used by the wizard
   *  Assignment step to derive customer scope. */
  tenantId?: string;
}

export function mapApiTagMinimal(t: any): MappedTagMinimal {
  return {
    id: t.tag_id || t.tagId || t.id || "",
    name: t.tag_name || t.tagName || t.name || "",
    color: t.tag_color || t.color || "#6b7280",
    icon: t.tag_icon || t.icon,
    type: String(t.tag_type || t.tagType || t.type || "static").toLowerCase(),
    description: t.tag_description || t.description || undefined,
    tenantId: t.tenantId || t.tenant_id || undefined,
  };
}

// --- Assignment shape (TagAssignmentDialog) ---
export interface MappedTagAssignment {
  id: string;
  name: string;
  description?: string;
  color: string;
  type: string;
  customers: string[];
}

export function mapApiTagAssignment(t: any): MappedTagAssignment {
  let customers: string[] = t.customers || [];
  if (customers.length === 0) {
    const tenantId = t.tenantId || t.tenant_id;
    if (tenantId) {
      customers = [tenantId];
    }
  }

  return {
    id: t.tag_id || t.tagId || t.id || "",
    name: t.tag_name || t.tagName || t.name || "",
    description: t.tag_description || t.description,
    color: t.tag_color || t.color || "#6b7280",
    type: String(t.tag_type || t.tagType || t.type || "static").toLowerCase(),
    customers,
  };
}
